import { Alerta, Badge, Button, Card, DataList, Dialog, MontoField, Row, Select, Stack, TextField } from "@/components";
import type { Caja, Moneda, SaldoCaja } from "@/lib/api";
import { opcionesCaja } from "@/lib/cajas";
import { formatFecha, formatMoneda } from "@/lib/format";
import type { PanelCajas as Panel } from "./usePanelCajas";

export interface PanelCajasProps {
  panel: Panel;
  /** Saldo de cada caja (de `/caja/saldos`), en el orden de la lista. */
  saldos: SaldoCaja[];
  cajas: Caja[];
  esAdmin: boolean;
}

/** Card "Cajas" de la pantalla Caja con sus diálogos (alta, renombrar, borrar, transferir). Admin y contador la ven. */
export function PanelCajas({ panel: p, saldos, cajas, esAdmin }: PanelCajasProps) {
  const d = p.dialogo;
  const activas = cajas.filter((c) => c.activo).length;

  const columnas = [
    {
      clave: "nombre",
      titulo: "Caja",
      render: (c: SaldoCaja) => (
        <Row envolver espacio={2}>
          <span>{c.nombre}</span>
          {c.principal && <Badge tono="primario">Principal</Badge>}
          {!c.activo && <Badge>Archivada</Badge>}
        </Row>
      ),
    },
    { clave: "ars", titulo: "Pesos", render: (c: SaldoCaja) => formatMoneda(c.saldo_ars, "ARS") },
    { clave: "usd", titulo: "Dólares", render: (c: SaldoCaja) => formatMoneda(c.saldo_usd, "USD") },
  ];

  const acciones = (c: SaldoCaja) => (
    <>
      <Button variante="secundario" tamanio="sm" aria-label={`Renombrar ${c.nombre}`} onClick={() => p.abrirCaja(c)}>
        Renombrar
      </Button>
      {!c.principal && c.activo && (
        <Button variante="secundario" tamanio="sm" aria-label={`Hacer principal ${c.nombre}`} onClick={() => p.hacerPrincipal(c)}>
          Hacer principal
        </Button>
      )}
      {!c.principal && (
        <Button variante="secundario" tamanio="sm" aria-label={`${c.activo ? "Archivar" : "Reactivar"} ${c.nombre}`} onClick={() => p.archivar(c)}>
          {c.activo ? "Archivar" : "Reactivar"}
        </Button>
      )}
      {!c.principal && (
        <Button variante="fantasma" tamanio="sm" aria-label={`Borrar ${c.nombre}`} onClick={() => p.abrirBorrarCaja(c)}>
          Borrar
        </Button>
      )}
    </>
  );

  const opciones = (actual: number | null) => opcionesCaja(cajas, actual);
  const transferencia = d?.tipo === "transferencia" ? d.transferencia : null;

  return (
    <Card>
      <Stack espacio={3}>
        <Row justificar="entre" envolver espacio={3}>
          <h2 className="text-titulo font-light tracking-tight">Cajas</h2>
          {esAdmin && (
            <Row espacio={2}>
              {activas > 1 && (
                <Button variante="secundario" onClick={p.abrirTransferencia}>
                  Transferir
                </Button>
              )}
              <Button variante="secundario" onClick={() => p.abrirCaja(null)}>
                Nueva caja
              </Button>
            </Row>
          )}
        </Row>
        <p className="text-chico text-texto-suave">Dónde está la plata. Lo que no elige caja va a la principal.</p>
        <Alerta texto={p.aviso} tono="alerta" />
        <Alerta texto={p.errorAccion ?? p.errorCargarTransferencia} />
        <DataList etiqueta="Cajas" columnas={columnas} filas={saldos} claveFila={(c) => c.id} acciones={esAdmin ? acciones : undefined} vacio="Todavía no hay cajas." />
      </Stack>

      <Dialog abierto={d?.tipo === "caja"} onCambio={(abierto) => !abierto && p.cerrar()} titulo={d?.tipo === "caja" && d.caja ? "Renombrar caja" : "Nueva caja"}>
        <form onSubmit={(e) => void p.guardarCaja(e)} noValidate>
          <Stack espacio={4}>
            <TextField etiqueta="Nombre" ayuda="Ej.: Banco Galicia, Efectivo Javier" value={p.nombre} onChange={(e) => p.setNombre(e.target.value)} error={p.errorNombre} />
            <Alerta texto={p.errorGuardarCaja} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={p.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={p.guardandoCaja}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={d?.tipo === "borrarCaja"} onCambio={(abierto) => !abierto && p.cerrar()} titulo="Borrar caja">
        {d?.tipo === "borrarCaja" && (
          <Stack espacio={4}>
            <p>{`¿Borrar la caja ${d.caja.nombre}? Si tiene movimientos, en vez de borrarse queda archivada.`}</p>
            <Alerta texto={p.errorBorrarCaja} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={p.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={p.borrandoCaja} onClick={() => void p.confirmarBorrarCaja()}>
                Borrar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>

      <Dialog abierto={d?.tipo === "transferencia"} onCambio={(abierto) => !abierto && p.cerrar()} titulo={transferencia ? "Editar transferencia" : "Transferir entre cajas"}>
        <form onSubmit={(e) => void p.guardarTransferencia(e)} noValidate>
          <Stack espacio={4}>
            <Select etiqueta="Sale de" opciones={opciones(transferencia?.origen_id ?? null)} valor={p.form.origen_id} onCambio={(v) => p.cambiar("origen_id", v)} placeholder="Elegí la caja" error={p.errores.origen_id} />
            <Select etiqueta="Entra en" opciones={opciones(transferencia?.destino_id ?? null)} valor={p.form.destino_id} onCambio={(v) => p.cambiar("destino_id", v)} placeholder="Elegí la caja" error={p.errores.destino_id} />
            <TextField etiqueta="Fecha" type="date" value={p.form.fecha} onChange={(e) => p.cambiar("fecha", e.target.value)} error={p.errores.fecha} />
            <MontoField etiqueta="Monto" valor={p.form.monto} onCambio={(v) => p.cambiar("monto", v)} moneda={p.form.moneda} onCambioMoneda={(m: Moneda) => p.cambiar("moneda", m)} error={p.errores.monto} />
            <TextField etiqueta="Nota" ayuda="Opcional" value={p.form.nota} onChange={(e) => p.cambiar("nota", e.target.value)} error={p.errores.nota} />
            <Alerta texto={p.errorGuardarTransferencia} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={p.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={p.guardandoTransferencia}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={d?.tipo === "borrarTransferencia"} onCambio={(abierto) => !abierto && p.cerrar()} titulo="Borrar transferencia">
        {d?.tipo === "borrarTransferencia" && (
          <Stack espacio={4}>
            <p>{`¿Borrar la transferencia de ${formatMoneda(d.transferencia.monto, d.transferencia.moneda)} del ${formatFecha(d.transferencia.fecha)}? No se puede deshacer.`}</p>
            <Alerta texto={p.errorBorrarTransferencia} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={p.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={p.borrandoTransferencia} onClick={() => void p.confirmarBorrarTransferencia()}>
                Borrar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>
    </Card>
  );
}
